import { useQueryClient } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'
import Alert from '@web-ui-kit/alert'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import NoEmailNotification from './Notifications/NoEmailNotification'
import SatisfactionSurveyNotification from './Notifications/SatisfactionSurveyNotification'
import PendingTasks from './Notifications/PendingTasks'
import ChatIANotification from './Notifications/ChatIANotification'
import { USER_KEYS, usePostResendEmail } from 'hooks/user'
import { TYPES_ALERTS, TYPES_USER_STATUS } from 'utils/constants'
import { getCurrentUserTypeByRole } from 'utils'
import { STATUS_FLAGS } from 'utils/constants/flags'
import { useSnackBarFeedback } from 'context/snackbarFeedback'
import useCheckCanary from 'hooks/useCheckCanary'
const NotificationWrapper = ({ children, type }) => {
  return type === 'alert' ? (
    <Alert description={children} variant='warning' />
  ) : (
    children
  )
}

NotificationWrapper.defaultProps = {
  type: 'alert',
}
NotificationWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  type: PropTypes.oneOf(['alert', 'custom']),
}

const NotificationsTop = () => {
  const addFeedback = useSnackBarFeedback()
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const queryClient = useQueryClient()
  const {
    email,
    id: userId,
    notifications,
    role,
    status,
    userFlags,
  } = queryClient.getQueryData({
    queryKey: [USER_KEYS.GET_USER],
  })

  const { mutate: mutateResendEmail } = usePostResendEmail()
  const isPendingToValidate = status === TYPES_USER_STATUS.PENDING_VERIFY
  const { isStudent, isTeacher } = getCurrentUserTypeByRole(role)
  const displayChatIAReminder = useCheckCanary(
    window?.chatIA?.canaryUsers,
    window?.chatIA?.showChatIAReminder
  )
  const handleSendEmail = () => {
    mutateResendEmail(
      { userId },
      {
        onSuccess: () => {
          addFeedback({
            dataTestId: 'success-feedback-update-grade',
            id: 'success-feedback-update-grade',
            text: t(
              'Te hemos enviado un correo con las indicaciones para verificar tu cuenta Comprueba que está escrito correctamente o que no lo has recibido en la bandeja de spam'
            ),
            variant: TYPES_ALERTS.SUCCESS,
          })
        },
      }
    )
  }

  const renderNotificationsAsTeachers = () => {
    const { enableSatisfactionSurvey } = window.satisfactionSurvey || {}

    return (
      <>
        {userFlags?.FEEDBACK_POOL_LATER === STATUS_FLAGS.TRUE &&
          enableSatisfactionSurvey && (
            <NotificationWrapper type='custom' userId={userId}>
              <SatisfactionSurveyNotification />
            </NotificationWrapper>
          )}
        {userFlags?.CHATIA_REMINDER === STATUS_FLAGS.TRUE &&
          displayChatIAReminder && (
            <NotificationWrapper type='custom' userId={userId}>
              <ChatIANotification />
            </NotificationWrapper>
          )}
      </>
    )
  }

  const renderNotificationsAsStudents = () => {
    const { tasks } = notifications || {}

    return (
      !!tasks?.length && (
        <NotificationWrapper type='custom'>
          {tasks.map(({ clazz, endDate, taskId, title }) => (
            <PendingTasks
              clazz={clazz}
              endDate={endDate}
              key={taskId}
              taskId={taskId}
              title={title}
              userId={userId}
            />
          ))}
        </NotificationWrapper>
      )
    )
  }

  return (
    <>
      {isPendingToValidate && (
        <NotificationWrapper userId={userId}>
          <NoEmailNotification
            email={email}
            onPress={handleSendEmail}
            pathname={pathname}
          />
        </NotificationWrapper>
      )}
      {isStudent && renderNotificationsAsStudents()}
      {isTeacher && renderNotificationsAsTeachers()}
    </>
  )
}

export default NotificationsTop
